import React, { Component } from 'react';
import { func, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Translate } from 'react-localize-redux';
import {
  withLang,
  langType,
  getMessages
} from '../../../i18n';
import {
  SurveySchemaEditorType,
  surveyTypeValues,
  publicationStatusValues,
  previousLocationType
} from '../../../constants/types';
import {
  getOrCheckSurveySchemaCodeAction,
  clearSurveySchemaOptionsAction,
  updateSurveySchemaOptionsAction,
  addErrorMessageAction,
  postSurveySchemaAction,
  updateSurveySchemaPartsAction,
  setTaskFulfilled,
  setTaskPending
} from '../../../actions';
import TextInput from '../../TextInput';
import { LocalizedInput, LocalizedTextArea } from '../../LocalizedInput';
import ConfirmButton from '../../ConfirmButton';
import FoldableList from '../../FoldableList';
import { COPY_SUFFIX, routes } from '../../../constants';
import { getLatestSchemaPartVersionsBySchemaId, getSurveySchema } from '../../../api';
import {
  getCompiledPath, makeFormCacheKey, getLocalizedText, getRouteSuffix, setDocumentTitle, getSubRouteForActionType, getActionType
} from '../../../util';
import EditorInfoBox from './InfoBox';
import EmailMessageInput from '../../EmailMessageInput';
import InfoBox from '../../InfoBox';
import InfoRow from '../../InfoBox/InfoRow';
import withRouter from '../../WithRouter';
import { withPreviousLocation } from '../../PreviousLocationProvider';

import styles from '../editor.module.css';

const TITLE = 'title';
const IDENTIFIER = 'code';
const TYPE = 'type';
const DESCRIPTION = 'description';
const INTRODUCTION = 'introduction';
const CONCLUSION = 'conclusion';
const FEEDBACK_INTRODUCTION = 'feedbackIntroduction';
const IN_LANGUAGE_NAMESPACE = 'inLanguage';

const checkForUpdatableSchemaParts = (latestSchemaPartVersions, surveySchema) => {
  const isLaterVersion = (oldVersion, latestVersion) =>
    oldVersion.code === latestVersion.code && oldVersion.version < latestVersion.version;
  const hasLaterVersion = (schemaPart) =>
    latestSchemaPartVersions.some((latestVersion) => isLaterVersion(schemaPart, latestVersion));
  const updatableSchemaParts = surveySchema.surveySchemaParts
    .map((sp) => ({ ...sp, hasLaterVersion: hasLaterVersion(sp) }));

  return { ...surveySchema, surveySchemaParts: updatableSchemaParts };
};

class SurveySchemaEditor extends Component {
  constructor(props) {
    super(props);

    this.fetchSurveySchemaData = this.fetchSurveySchemaData.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onLocalizedInputChange = this.onLocalizedInputChange.bind(this);
    this.onLocalizedRTEInputChange = this.onLocalizedRTEInputChange.bind(this);
    this.onCodeInputBlur = this.onCodeInputBlur.bind(this);
    this.onRemoveSchemaPart = this.onRemoveSchemaPart.bind(this);
    this.onSurveyTypeChange = this.onSurveyTypeChange.bind(this);
    this.onUpdateSchemaPartVersion = this.onUpdateSchemaPartVersion.bind(this);
    this.onRemoveExtraSurveyPart = this.onRemoveExtraSurveyPart.bind(this);
    this.renderSurveySchemaPartsSegment = this.renderSurveySchemaPartsSegment.bind(this);
    this.renderFeedbackIntroductionSegment = this.renderFeedbackIntroductionSegment.bind(this);
    this.renderSchemaUpdateInformationBox = this.renderSchemaUpdateInformationBox.bind(this);
    this.renderSurveySchemaPartRow = this.renderSurveySchemaPartRow.bind(this);
    this.renderSurveySchemaPartRows = this.renderSurveySchemaPartRows.bind(this);

    const currentRouteSuffix = getRouteSuffix(props.location.pathname);
    const isCreateNewVersion = currentRouteSuffix === getRouteSuffix(routes.surveySchemas.schemas.schema.newVersion.index);
    const isCreateNewCopy = currentRouteSuffix === getRouteSuffix(routes.surveySchemas.schemas.schema.copy.index);
    const actionType = getActionType(isCreateNewCopy, isCreateNewVersion);

    const type = props.surveySchema.type || surveyTypeValues.DEGREE_PROGRAM_SURVEY;

    this.state = {
      codeInputValid: true,
      previousCode: '',
      submitError: false,
      schemaInUse: false,
      formCacheKey: makeFormCacheKey(),
      isCreateNewVersion,
      isCreateNewCopy,
      actionType,
      readyToEdit: false,
      typeChangeDerivedIntroduction: getMessages(`defaultSurveyIntroduction.${type}`),
      typeChangeDerivedConclusion: getMessages(`defaultSurveyConclusion.${type}`)
    };
  }

  async componentDidMount() {
    const {
      surveySchema, params, dispatchGetCode, dispatchUpdate, previousLocation
    } = this.props;
    const isReturningFromNestedRoute =
      previousLocation
      && !![
        getRouteSuffix(routes.surveySchemas.surveyParts.index),
        getRouteSuffix(routes.surveySchemas.surveyParts.create.preview),
        getRouteSuffix(routes.surveySchemas.extraSurveyParts.index)
      ].find((route) => previousLocation.pathname.endsWith(route));

    if (params.surveySchemaId && !isReturningFromNestedRoute) {
      await this.fetchSurveySchemaData(params.surveySchemaId);
    } else if (!surveySchema.code) {
      const codeResult = await dispatchGetCode();
      await dispatchUpdate({
        code: codeResult.value.code
      });
    }
    this.setState({ readyToEdit: true });
  }

  onCancel() {
    const {
      dispatchClear, params, location, navigate
    } = this.props;

    const { surveySchemaId } = params;
    const entryReferer =
      (location.pathname === routes.surveySchemas.schemas.schema.edit.index
      || location.pathname === routes.surveySchemas.schemas.schema.newVersion.index)
        ? getCompiledPath(routes.surveySchemas.schemas.schema.index, { surveySchemaId })
        : routes.surveySchemas.schemas.index;

    dispatchClear();
    navigate(entryReferer);
  }

  onChange(event) {
    this.props.dispatchUpdate({ [event.target.id]: event.target.value });
  }

  onLocalizedInputChange(lang) {
    return (event) =>
      this.props.dispatchUpdate({ [event.target.name]: { [lang]: event.target.value } });
  }

  onLocalizedRTEInputChange(lang, name) {
    return (value) =>
      this.props.dispatchUpdate({ [name]: { [lang]: value } });
  }

  onCodeInputBlur(event) {
    const changed = event.target.value;
    if (changed === '') {
      this.setState({
        codeInputValid: false,
        previousCode: changed
      });
    } else if (changed !== this.state.previousCode) {
      this.setState({ previousCode: changed });
      this.props.dispatchGetCode(changed)
        .then((result) =>
          this.setState({ codeInputValid: result.value.available && result.value.valid }))
        .catch((err) => this.props.dispatchAddErrorMessage(err));
    }
  }

  onRemoveSchemaPart(schemaPartId) {
    const { surveySchema, dispatchUpdateSchemaParts } = this.props;
    const schemaParts = surveySchema.surveySchemaParts.filter((p) => p.id !== schemaPartId);
    dispatchUpdateSchemaParts(schemaParts);
  }

  onSurveyTypeChange(event) {
    const type = event.target.value;
    this.props.dispatchUpdate({ type });
    this.setState({
      typeChangeDerivedIntroduction: getMessages(`defaultSurveyIntroduction.${type}`),
      typeChangeDerivedConclusion: getMessages(`defaultSurveyConclusion.${type}`)
    });
  }

  onUpdateSchemaPartVersion(schemaPartCode) {
    const { latestSchemaPartVersions } = this.state;
    const { surveySchema, dispatchUpdateSchemaParts } = this.props;
    const schemaParts = surveySchema.surveySchemaParts.map((sp) => {
      const latestVersion = schemaPartCode === sp.code
        && latestSchemaPartVersions.find((lv) => schemaPartCode === lv.code);
      return latestVersion || sp;
    });

    dispatchUpdateSchemaParts(schemaParts);
  }

  onRemoveExtraSurveyPart() {
    this.props.dispatchUpdate({ extraSurveyPart: null });
  }

  async fetchSurveySchemaData(surveySchemaId) {
    const {
      dispatchSetTaskPending,
      dispatchSetTaskFulfilled,
      dispatchAddErrorMessage,
      dispatchUpdate
    } = this.props;
    const { isCreateNewVersion, isCreateNewCopy } = this.state;
    try {
      dispatchSetTaskPending();

      let surveySchema = await getSurveySchema(surveySchemaId);
      let latestSchemaPartVersions;

      if (isCreateNewVersion) {
        latestSchemaPartVersions = await getLatestSchemaPartVersionsBySchemaId(surveySchemaId);
        surveySchema = checkForUpdatableSchemaParts(latestSchemaPartVersions, surveySchema);
      } else if (isCreateNewCopy) {
        ['createdDate', 'createdBy', 'lastModifiedBy'].forEach((propName) => delete surveySchema[propName]);
        surveySchema.status = publicationStatusValues.DRAFT;
        surveySchema.version = 1;
        surveySchema.code += COPY_SUFFIX;
        surveySchema.useCount = 0;
        surveySchema.id = null;
      }
      surveySchema.isExistingSchema = !isCreateNewCopy;
      surveySchema.isNewVersion = isCreateNewVersion;
      surveySchema.isNewCopy = isCreateNewCopy;
      const schemaInUse = surveySchema.useCount > 0;
      const formCacheKey = makeFormCacheKey();

      this.setState({ schemaInUse, latestSchemaPartVersions, formCacheKey });
      dispatchUpdate(surveySchema);
    } catch (e) {
      dispatchAddErrorMessage(e);
    } finally {
      dispatchSetTaskFulfilled();
    }
  }

  renderSurveySchemaPartRow(removeFunction) {
    const {
      translate: t, lang
    } = this.props;
    const {
      schemaInUse, isCreateNewVersion
    } = this.state;
    const lockedFromEditing = schemaInUse && !isCreateNewVersion;

    return (schemaPart) => (
      <tr key={schemaPart.id} className={styles.row}>
        <td />
        <td>{schemaPart.code}</td>
        <td>{getLocalizedText(schemaPart.title, lang, t('surveyParts.titleMissing'))}</td>
        <td>{getLocalizedText(schemaPart.description, lang, t('surveyParts.descriptionMissing'))}</td>
        <td>
          {!lockedFromEditing && (
            <button
              type="button"
              onClick={() => removeFunction(schemaPart.id)}
              className="icon--trash iconButton"
              title={t('common.remove')}
            />
          )}
        </td>
      </tr>
    );
  }

  renderSurveySchemaPartRows() {
    return (surveySchemaParts) => (
      <table className={styles.listContainer}>
        <tbody>
          {surveySchemaParts.map(this.renderSurveySchemaPartRow(this.onRemoveSchemaPart))}
        </tbody>
      </table>
    );
  }

  renderSurveySchemaPartsSegment() {
    const {
      translate: t, surveySchema, params
    } = this.props;
    const {
      schemaInUse, isCreateNewVersion
    } = this.state;

    const hasSchemaParts = surveySchema.surveySchemaParts.length > 0;
    const schemaPartFoldThreshold = 10;
    const lockedFromEditing = schemaInUse && !isCreateNewVersion;
    return (
      <div>
        <h2 className={styles.subSection}>{t('editor.surveySchemaParts.schemaParts')}</h2>
        <div className={styles.infoItem}>{t('editor.surveySchemaParts.schemaPartsInfo')}</div>
        { hasSchemaParts
        && (
          <FoldableList
            foldThreshold={schemaPartFoldThreshold}
            noElementsMessage="Ei elementtejä"
            renderFunction={this.renderSurveySchemaPartRows()}
          >
            {surveySchema.surveySchemaParts}
          </FoldableList>
        )}
        {!lockedFromEditing
        && (
        <div className={styles.actions}>
          <Link
            className={`button--action ${hasSchemaParts ? 'icon--edit' : 'icon--plus'}`}
            to={surveySchema.id
              ? getCompiledPath(getSubRouteForActionType(this.state.actionType).surveyParts, { surveySchemaId: params.surveySchemaId })
              : routes.surveySchemas.schemas.create.surveyParts}
            data-cy="edit-schema-parts-button"
          >
            {t('editor.surveySchemaParts.editSchemaParts')}
          </Link>
        </div>
        )}
      </div>
    );
  }

  renderFeedbackIntroductionSegment() {
    const { translate: t, surveySchema } = this.props;

    return (
      <div>
        <h2 className={styles.subSection}>{t('editor.surveySchema.feedbackIntroduction')}</h2>
        <div className={styles.infoItem}>{t('editor.surveySchema.feedbackIntroductionInfo')}</div>
        <EmailMessageInput
          messageContentName={FEEDBACK_INTRODUCTION}
          defaultContent={getMessages(`defaultFeedbackIntroductionContent.${surveySchema.type}`)}
          isSchema
        />
      </div>
    );
  }

  renderSchemaUpdateInformationBox() {
    const { translate: t } = this.props;
    const { schemaInUse, isCreateNewVersion } = this.state;

    const titleCode = isCreateNewVersion
      ? 'editor.surveySchema.newVersion.infoTitle'
      : 'editor.surveySchema.edit.infoTitle';

    const textCode = isCreateNewVersion
      ? 'editor.surveySchema.newVersion.infoContent'
      : `editor.surveySchema.edit.${schemaInUse ? 'inUseExplanation' : 'notInUseExplanation'}`;

    setDocumentTitle(t(titleCode));

    return (
      <EditorInfoBox title={t(titleCode)} text={t(textCode)} />
    );
  }

  render() {
    const { translate: t, surveySchema, params } = this.props;
    const {
      codeInputValid, schemaInUse, formCacheKey, isCreateNewVersion,
      readyToEdit, typeChangeDerivedIntroduction, typeChangeDerivedConclusion
    } = this.state;

    if (!readyToEdit) { // wait until schema has loaded, or code has been retrieved
      return null;
    }

    const identifierErrorClass = codeInputValid ? '' : styles.error;
    const lockedFromEditing = schemaInUse || isCreateNewVersion;
    const showTypeDropdown = !!surveySchema.id;
    const defaultSurveyInvitationContent = getMessages(`defaultSurveyInvitationContent.${surveySchema.type}`);
    setDocumentTitle(t('surveySchemas.schema'));

    return (
      <div className="page-container">
        <h1>{t('surveySchemas.schema')}</h1>
        {!showTypeDropdown && (
          <InfoBox>
            <InfoRow title={t('common.type')} content={t(`surveyType.${surveySchema.type}`)} />
          </InfoBox>
        )}
        { (surveySchema.isExistingSchema || isCreateNewVersion)
            && this.renderSchemaUpdateInformationBox()}
        <form className="form" key={formCacheKey}>
          <div className={styles.formItem}>
            <h3>{t('editor.title')}</h3>
            <LocalizedInput
              name={TITLE}
              values={surveySchema.title}
              onInputChange={this.onLocalizedInputChange}
              placeholderTranslationNamespace={IN_LANGUAGE_NAMESPACE}
              maxLength={255}
              disabled={lockedFromEditing}
              useAccordion
              dataCyPrefix="survey-schema-editor-title-input"
            />
          </div>
          <div className={styles.contentContainer}>
            <div className={styles.contentColumn}>
              <div className={styles.formItem}>
                <label htmlFor={IDENTIFIER}>{t('editor.identifier')}</label>
                {!this.state.codeInputValid
                  && (
                  <div className={`${styles.errorContainer} ${identifierErrorClass}`}>
                    <p>{t('editor.identifierError')}</p>
                  </div>
                  )}
                <TextInput
                  id={IDENTIFIER}
                  className={identifierErrorClass}
                  placeholder={t('editor.surveySchema.identifierPlaceholder')}
                  maxLength={255}
                  value={surveySchema.code}
                  onChange={this.onChange}
                  onBlur={this.onCodeInputBlur}
                  disabled={lockedFromEditing}
                  dataCy="survey-schema-editor-identifier-input"
                />
              </div>
            </div>
            {showTypeDropdown && (
              <div className={styles.contentColumn}>
                <div className={styles.formItem}>
                  <label htmlFor={TYPE}>{t('common.type')}</label>
                  <select
                    id={TYPE}
                    value={surveySchema.type}
                    onChange={this.onSurveyTypeChange}
                    disabled={lockedFromEditing}
                  >
                    <option value="" disabled="disabled">{t('editor.surveySchema.typePlaceholder')}</option>
                    {Object.values(surveyTypeValues).map((type) => (
                      <option key={type} value={type}>{t(`surveyType.${type}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className={styles.formItem}>
            <h3>{t('editor.description')}</h3>
            <LocalizedTextArea
              name={DESCRIPTION}
              values={surveySchema.description}
              onInputChange={this.onLocalizedInputChange}
              placeholderTranslationNamespace={IN_LANGUAGE_NAMESPACE}
              useAccordion
              dataCyPrefix="survey-schema-editor-description-input"
            />
          </div>

          <div className={styles.formItem}>
            <EmailMessageInput
              messageContentName={INTRODUCTION}
              defaultContent={typeChangeDerivedIntroduction}
              isRichText
              customContentLabel={t('editor.surveySchema.introduction')}
              isSchema
              dataCyPrefix="survey-schema-editor-introduction-email-title-input"
            />
          </div>
          <div className={styles.formItem}>
            <EmailMessageInput
              messageContentName={CONCLUSION}
              defaultContent={typeChangeDerivedConclusion}
              isRichText
              customContentLabel={t('editor.surveySchema.completion')}
              isSchema
              dataCyPrefix="survey-schema-editor-completion-email-title-input"
            />
          </div>

          <div className={styles.formItem}>
            <h2 className={styles.subSection}>{t('editor.surveySchema.invitationEmailTitle')}</h2>
            <p><Translate id="startSurvey.email.invitationTitleDescription" /></p>
            <EmailMessageInput
              messageTitleName="invitationEmailTitle"
              messageContentName="invitationEmailContent"
              defaultTitle={getMessages(`defaultSurveyInvitationTitle.${surveySchema.type}`)}
              defaultContent={defaultSurveyInvitationContent}
              isSchema
              dataCyPrefix="survey-schema-editor-invitation-email"
            />
          </div>

          {this.renderSurveySchemaPartsSegment()}

          <h2 className={styles.subSection}>{t('editor.surveySchema.extraSurveyPart')}</h2>
          {surveySchema.extraSurveyPart && (
            <table>
              <tbody>
                {this.renderSurveySchemaPartRow(this.onRemoveExtraSurveyPart)(surveySchema.extraSurveyPart)}
              </tbody>
            </table>
          )}

          <div className={styles.actions}>
            <Link
              className={`button button--action icon--plus ${surveySchema.extraSurveyPart ? 'disabled' : ''}`}
              to={surveySchema.id
                ? getCompiledPath(getSubRouteForActionType(this.state.actionType).extraSurveyPart, { surveySchemaId: params.surveySchemaId })
                : routes.surveySchemas.schemas.create.extraSurveyPart}
              data-cy="choose-extra-survey-parts-button"
            >
              {t('editor.surveySchema.addExtraSurveyPart')}
            </Link>
          </div>

          {this.renderFeedbackIntroductionSegment()}

          {this.state.submitError
            && (
            <div className={`${styles.errorContainer} ${styles.error}`}>
              {t('common.saveError')}
            </div>
            )}

          <nav className={styles.navigation}>
            <ConfirmButton modalText={t('confirmButton.confirmCancel')} onConfirm={this.onCancel}>
              {t('common.cancel')}
            </ConfirmButton>
            <Link
              className="button--action"
              to={surveySchema.id
                ? getCompiledPath(getSubRouteForActionType(this.state.actionType).preview, { surveySchemaId: params.surveySchemaId })
                : routes.surveySchemas.schemas.create.preview}
              data-cy="preview-and-save-button"
            >
              {t('common.previewAndSave')}
            </Link>
          </nav>
        </form>
      </div>
    );
  }
}

SurveySchemaEditor.propTypes = {
  navigate: func.isRequired,
  surveySchema: SurveySchemaEditorType.isRequired,
  dispatchGetCode: func.isRequired,
  dispatchUpdate: func.isRequired,
  dispatchUpdateSchemaParts: func.isRequired,
  dispatchClear: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  params: shape({
    surveySchemaId: string
  }).isRequired,
  location: shape({
    pathname: string
  }).isRequired,
  previousLocation: previousLocationType.isRequired,
  ...langType
};

const mapStateToProps = ({ surveySchema }) => ({
  surveySchema: surveySchema.surveySchemaOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchGetCode: (code) =>
    dispatch(getOrCheckSurveySchemaCodeAction(code)),
  dispatchUpdate: (surveySchemaOptions) =>
    dispatch(updateSurveySchemaOptionsAction(surveySchemaOptions)),
  dispatchUpdateSchemaParts: (surveySchemaParts) =>
    dispatch(updateSurveySchemaPartsAction(surveySchemaParts)),
  dispatchPostSurveySchema: (surveySchemaOptions) =>
    dispatch(postSurveySchemaAction(surveySchemaOptions)),
  dispatchClear: () =>
    dispatch(clearSurveySchemaOptionsAction()),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

export default withPreviousLocation(withRouter(connect(mapStateToProps, mapDispatchToProps)(withLang(SurveySchemaEditor))));
